import { IconArrowRight, IconLock } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Tag } from "../../components/ui/Tag";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import type { Api } from "../../services/api";

type Article = Api["get"]["/webapp/public/articles/:slug"]["output"]["body"];

/**
 * "Journal · year", the desktop source line of a result row and of an article
 * recap. Null when the paper has neither.
 */
export function sourceLine(article: {
  journal: { name: string } | null;
  publication_date: string | null;
}): string | null {
  const parts = [
    article.journal === null ? null : article.journal.name,
    article.publication_date === null
      ? null
      : new Date(article.publication_date).getUTCFullYear().toString(),
  ].filter((part) => part !== null);
  return parts.length === 0 ? null : parts.join(" · ");
}

/**
 * The paper a desktop visual abstract panel is about (Figma "article-recap"):
 * source line, access, title, and a way to the article itself. The creation
 * panel shows it before a poster exists, the viewer's panel beside the poster,
 * where the authors join it as the poster's source.
 */
export function VisualAbstractArticleRecap({
  article,
  roomId,
  authors,
}: {
  article: Article;
  roomId: string | null;
  authors: string | null;
}): React.ReactElement {
  const { t } = useTranslation();
  const history = useHistory();
  const { articleLink } = usePageLinks(roomId);
  const source = sourceLine(article);
  return (
    <div className="flex flex-col gap-1.5 rounded-2xl border border-solid border-ds-slate-100 bg-ds-slate-50 px-4 py-3.5">
      <div className="flex flex-wrap items-center gap-2">
        {source === null ? null : (
          <span className="font-dm-sans text-xs font-medium text-ds-slate-500">
            {source}
          </span>
        )}
        {article.isOpenAccess === true ? (
          <Tag size="sm" variant="mint" state="idle" icon={null}>
            {t("Open access")}
          </Tag>
        ) : (
          <Tag
            size="sm"
            variant="slate"
            state="idle"
            icon={<IconLock className="size-3" stroke={1.5} />}
          >
            {t("Closed access")}
          </Tag>
        )}
      </div>
      <p className="m-0 line-clamp-3 font-dm-sans text-ui-body font-semibold text-ds-slate-950">
        {article.title}
      </p>
      {authors === null ? null : (
        <p className="m-0 truncate font-dm-sans text-xs font-medium text-ds-slate-500">
          {authors}
        </p>
      )}
      {/* A push, not a replace: the article page's Back is `goBack()`, and it
          has to land on THIS entry, whose URL still names what the panel was
          showing. */}
      <button
        type="button"
        className="flex w-fit cursor-pointer items-center gap-1 border-none bg-transparent p-0 pt-1 font-dm-sans text-ui-note font-semibold text-ds-mint-800"
        onClick={() => {
          history.push(articleLink(article));
        }}
      >
        {t("View article")}
        <IconArrowRight className="size-3.5" />
      </button>
    </div>
  );
}
